<div class="tw:p-3 tw:pb-[60px] tw:sm:p-5 tw:sm:px-7 tw:sm:pb-10 tw:bg-[#ffffff] tw:min-h-full tw:box-border">
  @if (sessionReport) {
  <div class="tw:flex tw:items-start tw:justify-between tw:mb-[18px]">
    <h1 class="page-title">Lesson summary
      @if (brandName) {
        <span class="font-14 tw:ml-3 tag tag--light" id="brandBadge">{{brandName}}</span>
      }
    </h1>
    <div class="tw:flex tw:items-center tw:gap-2">
      <span class="tw:text-[#999] tw:text-xs">Status:</span>
      <!-- Same status → colour/icon map as the session calendar tile, so Rejected reads red here too. -->
      <app-session-status-pill [status]="session.status"></app-session-status-pill>
      @if (canDebug) {
        <button mat-stroked-button type="button" class="tw:!text-[12px]" (click)="openDebugDialog()">Debug</button>
      }
    </div>
  </div>

  <leap-v2-session-meta-card [session]="session"
                             [tutorCancelled]="tutorCancelled"
                             [tutorCancelledSummary]="tutorCancelledSummary">
  </leap-v2-session-meta-card>

  <!-- Visible to every role (tutor, client, staff): why the report came back and what to do next. -->
  @if (reportRejected && rejection) {
    <div id="rejectionBanner" class="tw:mb-4 tw:px-3 tw:py-2.5 tw:bg-[#fff5f5] tw:border tw:border-[#f5c0c0] tw:rounded-[3px] tw:text-xs tw:text-[#333] tw:leading-[1.7]">
      <strong class="tw:text-[#c00]">Report rejected</strong>
      @if (rejection.rejectedBy) { by {{ rejection.rejectedBy }} }
      on {{ rejection.rejectedDate | date: 'dd/MM/yyyy HH:mm' }}<br>
      <span class="tw:text-[#666]">{{ rejection.reason }}</span><br>
      <span>Correct the report below and press Finalise session to resubmit it.</span>
    </div>
  }

  <h2 class="section-title">Attendance</h2>

  <span class="tw:text-[13px] tw:text-[#888]">Must be submitted within 30 minutes of session start</span>
  
  <mat-card appearance="outlined" class="tw:overflow-hidden">
    @for (student of students; track student.id) {
    <leap-v2-attendance-card [student]="student"
                             [existingAttendance]="attendanceFor(student.id)"
                             [disabled]="attendanceLocked"
                             (attendanceSaved)="onAttendanceSaved($event)"
                             (attendanceCleared)="onAttendanceCleared($event)">
    </leap-v2-attendance-card>
    }

    <div class="tw:flex tw:flex-wrap tw:items-center tw:justify-between tw:gap-3 tw:p-[20px] tw:pb-[0px] tw:border-t tw:border-t-[#eee]">
      @if (!attendanceLocked) {
      <span class="tw:text-[13px] tw:text-[#888]">Record attendance for all learners to submit the register.</span>
      }
      <div class="tw:flex tw:flex-wrap tw:gap-2 tw:ml-auto">
        @if (!attendanceLocked) {
        <button mat-stroked-button
                color="warn"
                class="tw:flex-1 tw:min-w-20 tw:sm:flex-none"
                [disabled]="anyStudentAttendanceSaved"
                (click)="openTutorCancelledDialog()">
          Tutor cancelled
        </button>
        }
        @if (canUseConsultantNotes) {
        <button mat-stroked-button
                class="tw:flex-1 tw:min-w-20 tw:sm:flex-none"
                [disabled]="!attendanceSubmitted"
                (click)="openConsultantNotesDialog()">
          Consultant notes
        </button>
        }
        @if (!attendanceLocked) {
        <button mat-raised-button
                color="primary"
                class="tw:flex-1 tw:min-w-20 tw:sm:flex-none"
                [disabled]="!allStudentsHaveAttendance"
                (click)="submitAttendance()">
          Submit attendance
        </button>
        }
        <!-- Unlock attendance reopens the register only, and only once it is submitted: it is not the session
             report's Unlock below. -->
        @if (attendanceSubmitted) {
        <button *checkFeatureRender="'UnlockAttendance'"
                mat-raised-button
                class="tw:flex-1 tw:min-w-20 tw:sm:flex-none"
                (click)="unlockAttendance()">
          Unlock attendance
        </button>
        }
      </div>
    </div>
    <!-- The saved consultant notes, for internal staff only: they record what the consultant did about the register. -->
    @if (canUseConsultantNotes && session.consultantNotes) {
    <div id="consultantNotesSaved" class="tw:mx-[20px] tw:mt-3 tw:px-3 tw:py-2.5 tw:bg-[#f7f9fc] tw:border tw:border-[#dde5f0] tw:rounded-[3px] tw:text-xs tw:text-[#333] tw:leading-[1.7]">
      <strong>Consultant notes</strong>
      @if (session.notesLastUpdated) { <span class="tw:text-[#888]">updated {{ session.notesLastUpdated | date: 'dd/MM/yyyy HH:mm' }}</span> }<br>
      <span class="tw:whitespace-pre-line">{{ session.consultantNotes }}</span>
    </div>
    }
  </mat-card>

  <h2 class="section-title tw:py-3">Session report</h2>

  <mat-card appearance="outlined" class="tw:overflow-hidden">

    <!-- sessions.delivery-setting (39883): how this one session was delivered. Needed to finalise, not for attendance;
         it changes this session only, never the booking in Orbit. -->
    @if (deliverySettingOn) {
    <div class="tw:p-4 tw:border-b tw:border-b-[#eee]" id="deliverySettingSection">
      <label for="deliverySetting" class="tw:block tw:text-[11px] tw:font-semibold tw:text-[#555] tw:uppercase tw:tracking-[0.04em] tw:mb-3">
        How was this session delivered?
        @if (deliverySettingRequired) {
          <span class="tw:text-[11px] tw:font-normal tw:text-[#aaa] tw:normal-case tw:tracking-normal">(REQUIRED TO FINALISE)</span>
        }
      </label>
      <select id="deliverySetting" class="field__control tw:max-w-md"
        [(ngModel)]="sessionReport.report.deliverySetting"
        [ngModelOptions]="{standalone: true}"
        [disabled]="reportLocked">
        <option [ngValue]="null" disabled>Choose how the session was delivered</option>
        @for (setting of deliverySettings; track setting) {
          <option [ngValue]="setting">{{ setting }}</option>
        }
      </select>
      <span class="field__hint tw:block" id="deliverySettingHint">This changes this session only, not the booking in Orbit.</span>
      @if (deliverySettingRequired && !sessionReport.report.deliverySetting && !reportLocked) {
        <span class="field__error tw:block" id="deliverySettingRequired">Choose how this session was delivered before you finalise it.</span>
      }
    </div>
    }

    <div class="tw:p-4 tw:border-b tw:border-b-[#eee]">
      <div class="tw:text-[11px] tw:font-semibold tw:text-[#555] tw:uppercase tw:tracking-[0.04em] tw:mb-3">
        Learning Objectives
        @if (objectivesRequired) {
          <span class="tw:text-[11px] tw:font-normal tw:text-[#aaa] tw:normal-case tw:tracking-normal">(AT LEAST ONE REQUIRED)</span>
        } @else if (tutorCancelled) {
          <span class="tw:text-[11px] tw:font-normal tw:text-[#aaa] tw:normal-case tw:tracking-normal">(OPTIONAL — TUTOR CANCELLED)</span>
        } @else {
          <span class="tw:text-[11px] tw:font-normal tw:text-[#aaa] tw:normal-case tw:tracking-normal">(OPTIONAL — NO LEARNERS ATTENDED)</span>
        }
      </div>

      @for (objective of objectives; track $index; let i = $index) {
      <div class="tw:w-full tw:block tw:mb-2">
        <div class="tw:flex tw:items-center tw:justify-between">
          <label class="field__label">Learning objective {{ i + 1 }}</label>
          @if (i > 0) {
            <button
              type="button"
              mat-icon-button
              class="tw:!w-[24px] tw:!h-[24px] tw:!p-0 tw:!flex tw:!items-center tw:!justify-center tw:text-[#888]"
              [disabled]="reportLocked"
              (click)="removeObjective(i)"
              aria-label="Remove learning objective">
              <mat-icon class="tw:!text-[16px] tw:!w-[16px] tw:!h-[16px] tw:!leading-[16px]">close</mat-icon>
            </button>
          }
        </div>
        <textarea class="field__control tw:w-full"
                  [(ngModel)]="objectives[i]"
                  [ngModelOptions]="{standalone: true}"
                  [disabled]="reportLocked"
                  rows="2"
                  maxlength="1000">
            </textarea>
        <span class="field__hint tw:block tw:text-right">{{ objectives[i].length }} / 1000</span>
        @if (objectivesRequired && isObjectiveEmpty(i)) {
          <span class="field__error tw:block">Enter a learning objective</span>
        }
      </div>
      }

      @if (objectives.length < maxObjectives) {
        <button mat-stroked-button
                type="button"
                class="tw:!text-[13px] tw:!border tw:!border-dashed tw:!border-[#1565c0] tw:!text-[#1565c0]"
                [disabled]="reportLocked"
                (click)="addObjective()">
          + Add Objective {{ objectives.length + 1 }}
        </button>
      }

    </div>

    @if (attendanceSubmitted) {
    @for (student of students; track student.id) {
    @if (hasAttendance(student.id) && isStudentAbsent(student.id)) {
    <div class="tw:p-4 tw:border-b tw:border-b-[#eee] tw:text-[#aaa] tw:text-xs tw:italic">
      {{ student.name }} — no session report available (learner did not attend)
    </div>
    }
    @if (showStudentPanel(student.id)) {
    <leap-v2-student-panel [student]="student"
                           [objectives]="objectives"
                           [disabled]="reportLocked"
                           [concern]="concernFor(student.id)"
                           [canRaiseConcern]="canRaiseConcern"
                           (concernRaised)="onConcernRaised(student.id, $event)">
    </leap-v2-student-panel>
    }
    }
    }

    <!-- A cancelled session closes the footer entirely — a tutor cancellation (which cancels the session in
         Orbit itself) or a slot the coordinator cancelled in the Orbit diary. Nothing is submitted for either,
         so there is no Save/Finalise session, no Accept/Reject, and no Unlock: Orbit will not re-authorise a
         cancelled session, and a tutor-cancelled one has no rows to unlock (sessionId is 0). A session Orbit
         cancelled on Submit because every learner gave 1+ days' notice reads Submitted, not Cancelled, and
         keeps the normal footer below. -->
    @if (!tutorCancelled && !reportCancelled && (attendanceSubmitted || reportSubmitted || reportRejected || reportFinalised)) {
    <div class="tw:flex tw:flex-wrap tw:justify-end tw:gap-2 tw:p-4 tw:border-t tw:border-t-[#eee]">
      @if (reportFinalised) {
        <!-- Approved is terminal for sign-off — no Save/Finalise session, no Accept/Reject. Unlock is the one
             way back: a QA or client-reporting correction found after approval. (The other terminal state,
             Cancelled, closes the whole footer above, so only Approved reaches here.) -->
        <button *checkFeatureRender="'UnlockSession'" mat-stroked-button class="tw:flex-1 tw:min-w-20 tw:sm:flex-none" (click)="reopenSession()">Unlock</button>
      } @else if (reportSubmitted) {
        <!-- A submitted report is signed off by whoever holds the legacy tutor-report approval features
             (ADMIN/PRGMGR/TCMGR/TTRCRD — never tutors): Accept records the LEAP sign-off, Reject returns it
             to the tutor with a reason. Payment was authorised in Orbit on Submit, and neither button
             changes that. Each button is gated on its own feature; a tutor sees nothing. -->
        <!-- The report's Unlock is the quiet correction: reopen for editing without a rejection reason or the
             email, for when the consultant or QA is fixing the content rather than sending it back. The register
             stays submitted, so the report is still here to edit, with Save and Finalise session. -->
        <button *checkFeatureRender="'UnlockSession'" mat-stroked-button class="tw:flex-1 tw:min-w-20 tw:sm:flex-none" (click)="reopenSession()">Unlock</button>
        @if (canReject) {
          <button mat-stroked-button color="warn" class="tw:flex-1 tw:min-w-20 tw:sm:flex-none" (click)="rejectSession()">Reject</button>
        }
        @if (canApprove) {
          <button mat-raised-button color="primary" class="tw:flex-1 tw:min-w-20 tw:sm:flex-none" (click)="acceptSession()">Accept</button>
        }
      } @else {
        <!-- Open OR Rejected: Save / Finalise session, and nothing else. Neither is locked, so there is no Unlock
             here: a rejected report is corrected and resubmitted directly, and an unlocked one (by either Unlock)
             is awaiting finalisation. -->
        <button mat-stroked-button class="tw:flex-1 tw:min-w-20 tw:sm:flex-none" (click)="saveSession()">Save</button>
        <!-- Finalise session is always the tutor's final action: it settles the session in Orbit (authorised, or
             cancelled when every learner gave 1+ days notice) and lands on Submitted either way, so it confirms
             first with wording for the Orbit outcome. -->
        <button mat-raised-button color="primary" class="tw:flex-1 tw:min-w-20 tw:sm:flex-none" [disabled]="!reportComplete" (click)="submitProgress()">Finalise session</button>
      }
    </div>
    }

  </mat-card>
  } @else {
  <div class="tw:text-[13px] tw:text-[#888] tw:p-4">Loading session…</div>
  }

</div>
